import { IPromise } from 'angular'
import find from 'lodash/find'
import { ApiButtonLayout } from '@vend/types'

import { trackError } from '@vend/utilities'
import { PERMISSIONS } from '../constants'
import { STATES } from '../../../lib/routes'
import { $q, $state } from '../ng-compat/imports'
import * as buttonLayoutsResource from '../components/quick-keys/button-layouts-resource/button-layouts-resource'
import { EventEmitter } from '../util/event-emitter'
import { SyncData } from '../factories/database/entity-store'
import { quickKeyLayoutsStore } from '../factories/database/quick-key-layouts-store'
import { ApiLayoutData } from '../components/quick-keys/utils/qk-types'
import {
  expeditedSyncManager,
  registerManager,
  syncManager,
  userManager,
} from '../ng-compat/managers'
import { wrDebugToolsModuleRegistry } from '../factories/application/wr-debug-tools'
import { posStateManager } from './pos-state-manager'

interface QuickKeyLayoutChangeEvent {
  quickKeyLayout: $TSFixMe
}

type Events = {
  quickKeyLayoutChange: [QuickKeyLayoutChangeEvent]
  quickKeyCreateLayoutError: [void]
}

/**
 * Responsible for managing Quick Keys.
 *
 * @class QuickKeysManager
 * @constructor
 * @extends {EventEmitter}
 */
export class QuickKeysManager extends EventEmitter<Events> {
  private _areDuplicationsComplete?: () => void

  private _quickKeyLayout: ApiLayoutData | null = null
  private _whenInitialised?: angular.IPromise<[ApiLayoutData | null, void]>

  public defaultNewLayoutName = 'New Layout'

  /**
   * Initialise Quick Keys Manager.
   *
   * @method init
   *
   * @return {Promise} A promise resolved once the manager has completed initialisation
   */
  public init() {
    if (!this._whenInitialised) {
      this._whenInitialised = syncManager
        .whenCompleteEntityDataAvailable('quickKeyLayouts')
        .then(() => this._init())
    }
    return this._whenInitialised
  }

  /**
   * The quick key layout data for the current active register.
   *
   * @readOnly
   * @property quickKeyLayout
   * @type {Object}
   */
  public get quickKeyLayout() {
    return this._quickKeyLayout
  }

  /**
   * Whether the provided quick key layout is the current, active layout for the register.
   *
   * @method isCurrentLayout
   * @param  {quickKeyLayout} quickKeyLayout
   *
   * @return {Boolean} True if the provided layout is the current active layout
   */
  public isCurrentLayout(quickKeyLayout: ApiButtonLayout) {
    return Boolean(
      this._quickKeyLayout && this._quickKeyLayout.id === quickKeyLayout.id
    )
  }

  /**
   * Retrieve all quick key layout's for the currently active register.
   *
   * @method getAllQuickKeyLayouts
   *
   * @return {Promise<Array>} A promise resolved once all of the quick key layouts have been retrieved from
   *         the data store
   */
  public getAllQuickKeyLayouts() {
    return quickKeyLayoutsStore.all()
  }

  /**
   * Retrieve a quick key layout by its ID.
   */
  public getQuickKeyLayoutById(layoutId: string) {
    return quickKeyLayoutsStore.get(layoutId)
  }

  /**
   * Responsible for creating a new button layout. Creates a new layout via the API and adds the returned layout
   * to the store.
   *
   * @method createLayout
   *
   * @param  layoutName {String}
   *         The name of the new layout
   *
   * @return {Promise<ButtonLayout>} A promise resolved once the layout has been created and stored locally.
   */
  public createLayout(layoutName: string) {
    return buttonLayoutsResource
      .create(layoutName)
      .then(layout => {
        return quickKeyLayoutsStore.syncData([layout]).then(() => layout)
      })
      .catch(error => {
        trackError('[QuickKeysManager] Error creating new button layout', {
          data: { error },
        })
        // Need to emit an event so we can trigger a toast from
        // the QuickKeysSettings component as translation are not
        // available within the manager
        this.emit('quickKeyCreateLayoutError')
        return $q.reject(error)
      })
  }

  /**
   * Navigates to the edit layout screen, setting the newly created layout as the current layout if there are no other
   * layouts.
   *
   * @method _navigateToEditState
   *
   * @param  quickKeyLayout {QuickKeyLayout}
   *         The quick key layout to evaluate
   *
   * @param registerHasExistingLayouts {Boolean}
   *
   * @return {Promise} A promise resolved once the edit state has been navigated to.
   */
  public navigateToEditState(
    quickKeyLayout: ApiButtonLayout,
    isNewLayout: $TSFixMe
  ) {
    return this.getAllQuickKeyLayouts().then((layouts: $TSFixMe) => {
      const hasExistingLayouts = Boolean(layouts.length)
      let navigateToEditStateDeferred: IPromise<unknown> = $q.resolve()
      if (!hasExistingLayouts) {
        navigateToEditStateDeferred = registerManager.setCurrentQuickKeyLayout(
          quickKeyLayout
        )
      }
      return navigateToEditStateDeferred
        .catch(error => {
          trackError(
            '[AddQuickKeyLayout] Error setting new layout as the current layout.',
            { data: { error } }
          )
        })
        .finally(() => {
          return $state.go(STATES.SETTINGS_QUICK_KEYS_EDIT, {
            layoutId: quickKeyLayout.id,
            newLayout: isNewLayout || false,
          })
        })
    })
  }

  /**
   * Duplicate an existing quick key button layout. @todo handle polling for the background 'dupication' job to be
   * successful.
   *
   * @param  quickKeyLayout {quickKeyLayout}
   *         The quick key button layout to duplicate
   *
   * @return {Promise<ButtonLayout>} A promise resolved once the layout has been duplicated and stored locally.
   */
  public duplicateLayout(quickKeyLayout: ApiButtonLayout) {
    return buttonLayoutsResource
      .duplicate(quickKeyLayout)
      .then(layout => {
        posStateManager.setQkLayoutDuplication(layout.id)
        this._expediteDuplications()
      })
      .catch(error => {
        trackError('[QuickKeysManager] Error duplicating button layout', {
          data: { error },
        })
        return $q.reject(error)
      })
  }

  /**
   * Updates the quick key button layout with the details provided.
   *
   * @param  quickKeyLayoutId {String}
   *         The id of the quick key button layout to update
   *
   * @param  data {Object}
   *         The updated details for the layout
   *
   * @return {Promise<ButtonLayout>} A promise resolved once the layout has been updated and syncd locally.
   */
  public updateLayout(quickKeyLayoutId: string, data: any) {
    return buttonLayoutsResource
      .update(quickKeyLayoutId, data)
      .then(layout => {
        return quickKeyLayoutsStore.syncData([layout]).then(() => layout)
      })
      .catch(error => {
        trackError('[QuickKeysManager] Error updating button layout', {
          data: { error },
        })
        return $q.reject(error)
      })
  }

  /**
   * Delete an existing quick key button layout.
   *
   * @param quickKeyLayout {quickKeyLayout}
   *        The quick key button layout to remove
   *
   * @return {Promise} A promise resolved once the layout has been deleted and removed from the store.
   */
  public deleteLayout(quickKeyLayout: ApiButtonLayout) {
    return buttonLayoutsResource
      .deleteLayout(quickKeyLayout)
      .then(layout => quickKeyLayoutsStore.syncData([layout as $TSFixMe]))
      .catch(error => {
        trackError('[QuickKeysManager] Error deleting button layout', {
          data: { error },
        })
        return $q.reject(error)
      })
  }

  /**
   * Determine if quick keys is enabled.
   *
   * @method isQuickKeysEnabled
   *
   * @return {Boolean} True is quick keys is enabled for register, false otherwise.
   */
  public isQuickKeysEnabled() {
    const register = registerManager.getActiveRegister()
    return Boolean(register) && register.is_quick_keys_enabled
  }

  /**
   * Determine whether a retailer has permissions to manage quick keys
   *
   * @return {Boolean} True if the user has permission to manage quick keys
   */
  public hasPermission() {
    return userManager.hasPermission(PERMISSIONS.QUICK_KEYS_MANAGE)
  }

  // ============================== Private ============================== //
  /**
   * Complete initialisation of the quick keys manager. Binds events and resolves the initial state (button layout
   * and setup in progress status).
   *
   * @private
   * @method _init
   *
   * @return {Promise} A promise resolved once initialisation is complete
   */
  private _init() {
    registerManager.on(
      'activeRegisterChange',
      this._updateRegisterQuickKeysState,
      this
    )
    quickKeyLayoutsStore.on('sync', this._onQuickKeyLayoutSync, this)
    return $q.all([
      this._updateRegisterQuickKeysState(),
      this._updateDuplicationsInProgress().then(() =>
        this._expediteDuplications()
      ),
    ])
  }

  /**
   * Update the quick keys state (quick key layout and setup in progress status) for the active register.
   *
   * @private
   * @method _updateRegisterQuickKeysState
   *
   * @return {Promise} A promise resolved once the state has been updated for the active register
   */
  private _updateRegisterQuickKeysState() {
    return this._resolveLayout()
  }

  /**
   * Resolve the quick key layout for the active register.
   *
   * @private
   * @method _resolveLayout
   *
   * @return {Promise} A promise resolved once the layout has been identified and loaded and set on the manager
   */
  private _resolveLayout() {
    return $q((resolve, reject) => {
      const register = registerManager.getActiveRegister()
      const layoutId = register && register.button_layout_id
      if (layoutId) {
        quickKeyLayoutsStore
          .get(layoutId)
          .then(layout => {
            if (!layout) {
              // We may be in the middle of a sync (e.g. register updated with button_layout_id but actual button
              // layout hasn't been synced yet)
              return syncManager
                .whenCompleteEntityDataAvailable('quickKeyLayouts')
                .then(() => quickKeyLayoutsStore.get(layoutId))
            }
            return layout
          })
          .then(resolve, reject)
      } else {
        resolve(null)
      }
    }).then(quickKeyLayout => this._setLayout(quickKeyLayout))
  }

  /**
   * Set the given quick key layout as the active layout on the manager. Emits the `quickKeyLayoutChange` event if
   * the layout has changed.
   *
   * @private
   * @method _setLayout
   *
   * @param  quickKeyLayout {Object}
   *         The quick key layout to set
   *
   * @return {Object} The layout set on the manager
   */
  private _setLayout(quickKeyLayout: any) {
    const prevLayout = this._quickKeyLayout
    let layoutChanged
    if (prevLayout) {
      layoutChanged =
        !quickKeyLayout ||
        prevLayout.id !== quickKeyLayout.id ||
        prevLayout.version !== quickKeyLayout.version
    } else {
      layoutChanged = Boolean(quickKeyLayout)
    }
    this._quickKeyLayout = quickKeyLayout || null
    if (layoutChanged) {
      this.emit('quickKeyLayoutChange', { quickKeyLayout })
    }
    return this._quickKeyLayout
  }

  /**
   * Handle a sync event from the quickKeyLayoutsStore. Updates the quick key layout if it is updated or clears it
   * if it is deleted.
   *
   * @private
   * @method _onQuickKeyLayoutSync
   *
   * @param  event {Event}
   *         Details of what was updated/removed in the sync
   */
  private _onQuickKeyLayoutSync(event: SyncData<'quickKeyLayouts'>) {
    this._updateDuplicationsInProgress()
    if (!this._quickKeyLayout) {
      return
    }
    const id = this._quickKeyLayout.id
    const updatedLayout = find(event.updated, { id })
    if (updatedLayout) {
      this._setLayout(updatedLayout)
    } else {
      const removedLayout = find(event.removed, { id })
      if (removedLayout) {
        this._setLayout(null)
      }
    }
  }

  /**
   * Update the list of quick key duplications in progress, determining if each duplication is complete by checking
   * the layout's version.
   *
   * @private
   * @method _updateDuplicationsInProgress
   *
   * @return {Promise} A promise resolved once the list of duplications in progress has been updated
   */
  private _updateDuplicationsInProgress() {
    const dupes = posStateManager.getAllQkLayoutDuplications()
    return $q.all(dupes.map(layout => this._updateDuplicationStatus(layout)))
  }

  /**
   * Update the duplication status of the given button layout ID. If the button layout associated with the given ID
   * exists in the database and has a non-zero version then its duplication is complete.
   *
   * @private
   * @method _updateDuplicationStatus
   *
   * @param  {String} layoutId
   *         The ID of the button layout to update the duplication state thereof
   *
   * @return {Promise} A promise resolved once the duplication status of the button layout has been updated
   */
  private _updateDuplicationStatus(layoutId: string) {
    return quickKeyLayoutsStore.get(layoutId).then(layout => {
      const version = layout && layout.version
      if (version! > 0) {
        posStateManager.clearQkLayoutDuplication(layoutId)
      }
    })
  }

  /**
   * Schedule an expedited sync to wait for any quick key layout duplications to complete.
   *
   * @private
   * @method _expediteDuplications
   */
  private _expediteDuplications() {
    if (!this._areDuplicationsComplete) {
      this._areDuplicationsComplete = () =>
        !posStateManager.getAllQkLayoutDuplications().length
    }
    expeditedSyncManager
      .expedite({ isSyncComplete: this._areDuplicationsComplete })
      .catch(() => this._onDuplicationTimeout())
  }

  /**
   * Handle quick key layout duplications timing out (after 10 minutes since the last one was triggered). Clears all
   * active duplications - they will likely come through at a later point during a standard scheduled sync.
   *
   * @private
   * @method _onDuplicationTimeout
   */
  private _onDuplicationTimeout() {
    // Clear all active duplications as they haven't completed within 10 minutes - they will likely come through at a
    // later point during a standard scheduled sync
    posStateManager
      .getAllQkLayoutDuplications()
      .forEach((id: $TSFixMe) => posStateManager.clearQkLayoutDuplication(id))
  }
}

export const quickKeysManager = new QuickKeysManager()

wrDebugToolsModuleRegistry.register('quickKeysManager', quickKeysManager)
